import { useState } from "react";

import { CheckCircleIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";

import { getStrapiClient } from "@/lib/strapiClient";

import Logo from "../assets/images/logo-maladeta.svg";

export default function Settings() {
	const { t } = useTranslation();

	const [showSuccess, setShowSuccess] = useState(false);
	const [showError, setShowError] = useState(false);

	const formik = useFormik({
		initialValues: {
			currentPassword: "currentPassword",
			password: "userNewPassword",
			passwordConfirmation: "userNewPassword",
		},
		onSubmit: (values) => {
			const onSubmit = async () => {
				try {
					const client = getStrapiClient();
					const response = await client.fetch("auth/change-password", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(values),
					});
					if (!response.ok) throw new Error("change-password failed");
					setShowSuccess(true);
				} catch (_err) {
					setShowError(true);
				}
			};

			trackPromise(onSubmit());
		},
	});

	const PasswordChangeSuccesful = () => {
		return (
			<div className="mb-4 rounded-md bg-emerald-50 p-4">
				<div className="flex">
					<div className="shrink-0">
						<CheckCircleIcon className="h-5 w-5 text-emerald-400" aria-hidden="true" />
					</div>
					<div className="ml-3">
						<p className="font-medium text-emerald-800 text-sm">{t("settings.passwd_success")}</p>
					</div>
					<div className="ml-auto pl-3">
						<div className="-mx-1.5 -my-1.5">
							<button
								type="button"
								className="inline-flex rounded-md bg-emerald-50 p-1.5 text-emerald-500 hover:bg-emerald-100 focus:outline-hidden focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 focus:ring-offset-emerald-50"
								onClick={() => setShowSuccess(false)}
							>
								<span className="sr-only">Dismiss</span>
								<XMarkIcon className="h-5 w-5" aria-hidden="true" />
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const PasswordChangeError = () => {
		return (
			<div className="mb-4 rounded-md bg-red-50 p-4">
				<div className="flex">
					<div className="shrink-0">
						<XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
					</div>
					<div className="ml-3">
						<p className="font-medium text-red-800 text-sm">{t("settings.error")}</p>
					</div>
					<div className="ml-auto pl-3">
						<div className="-mx-1.5 -my-1.5">
							<button
								type="button"
								className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-hidden focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
								onClick={() => setShowError(false)}
							>
								<span className="sr-only">Dismiss</span>
								<XMarkIcon className="h-5 w-5" aria-hidden="true" />
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="space-y-6 p-10">
			<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
				<div className="md:grid md:grid-cols-3 md:gap-6">
					<div className="md:col-span-1">
						<h3 className="font-medium text-gray-900 text-lg leading-6">{t("settings.embed")}</h3>
					</div>
					<div className="mt-5 md:col-span-2 md:mt-0">
						<div className="bg-blue-50 p-5 text-md sm:rounded-lg">
							<code>
								{
									'<iframe src="https://backoffice.acna.cat" title="ACNA - Registre d\'accidents" scrolling="Yes" height="100%" width="100%" style="border: none;"></iframe>'
								}
							</code>
						</div>
					</div>
				</div>
			</div>

			<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
				{showSuccess && <PasswordChangeSuccesful />}
				{showError && <PasswordChangeError />}
				<div className="md:grid md:grid-cols-3 md:gap-6">
					<div className="md:col-span-1">
						<h3 className="font-medium text-gray-900 text-lg leading-6">{t("settings.change_passwd")}</h3>
						<p className="mt-1 text-gray-500 text-sm">{t("settings.change_passwd_subtitle")}</p>
					</div>
					<div className="mt-5 md:col-span-2 md:mt-0">
						<form className="space-y-6" onSubmit={formik.handleSubmit}>
							<div>
								<label htmlFor="currentPassword" className="block font-medium text-gray-700 text-sm">
									{t("settings.current_passwd")}
								</label>
								<div className="mt-1">
									<input
										id="currentPassword"
										name="currentPassword"
										type="password"
										required
										className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
										onChange={formik.handleChange}
										value={formik.values.currentPassword}
									/>
								</div>
							</div>
							<div>
								<label htmlFor="password" className="block font-medium text-gray-700 text-sm">
									{t("settings.new_passwd")}
								</label>
								<div className="mt-1">
									<input
										id="password"
										name="password"
										type="password"
										required
										className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
										onChange={formik.handleChange}
										value={formik.values.password}
									/>
								</div>
							</div>
							<div>
								<label htmlFor="passwordConfirmation" className="block font-medium text-gray-700 text-sm">
									{t("settings.confirm_new_passwd")}
								</label>
								<div className="mt-1">
									<input
										id="passwordConfirmation"
										name="passwordConfirmation"
										type="password"
										required
										className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
										onChange={formik.handleChange}
										value={formik.values.passwordConfirmation}
									/>
								</div>
							</div>
							<div className="flex justify-end pt-6">
								<button
									type="submit"
									className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
								>
									{t("settings.change_passwd")}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>

			<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
				<div className="mt-5 flex w-full flex-row place-content-center space-x-6 text-center md:col-span-2 md:mt-0">
					<a href="https://maladeta.studio">
						<img className="block h-12 w-auto" src={Logo} alt="Maladeta Studio" />
					</a>
				</div>
			</div>
		</div>
	);
}
